'use client';

import { Check, X, ThumbsUp, ThumbsDown, Minus, ArrowRight } from 'lucide-react';

// Do's and Don'ts two-column comparison
interface DosDontsProps {
  dos: string[];
  donts: string[];
  dosTitle?: string;
  dontsTitle?: string;
}

export function DosDonts({
  dos,
  donts,
  dosTitle = "Do this",
  dontsTitle = "Not this",
}: DosDontsProps) {
  return (
    <div className="my-10 not-prose">
      <div className="grid md:grid-cols-2 gap-4">
        {/* Do's */}
        <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
              <Check className="w-4 h-4 text-white" strokeWidth={3} />
            </div>
            <p className="font-semibold text-emerald-800">{dosTitle}</p>
          </div>
          <ul className="space-y-3">
            {dos.map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <ThumbsUp className="w-4 h-4 text-emerald-500 shrink-0 mt-1" />
                <span className="text-gray-700 text-sm leading-relaxed">
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Don'ts */}
        <div className="bg-red-50 rounded-xl p-5 border border-red-100">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
              <X className="w-4 h-4 text-white" strokeWidth={3} />
            </div>
            <p className="font-semibold text-red-800">{dontsTitle}</p>
          </div>
          <ul className="space-y-3">
            {donts.map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <ThumbsDown className="w-4 h-4 text-red-500 shrink-0 mt-1" />
                <span className="text-gray-700 text-sm leading-relaxed">
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// Pros and Cons list
interface ProsConsProps {
  pros: string[];
  cons: string[];
  title?: string;
}

export function ProsCons({ pros, cons, title }: ProsConsProps) {
  return (
    <div className="my-10 not-prose">
      <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
        {title && (
          <h3 className="font-semibold text-gray-900 mb-4 pb-4 border-b border-gray-200">
            {title}
          </h3>
        )}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Pros */}
          <div>
            <p className="text-sm font-medium text-emerald-600 mb-3 flex items-center gap-2">
              <Check className="w-4 h-4" />
              Pros
            </p>
            <ul className="space-y-2">
              {pros.map((pro, index) => (
                <li
                  key={index}
                  className="text-sm text-gray-700 leading-relaxed pl-6 relative"
                >
                  <span className="absolute left-0 top-1.5 w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                  {pro}
                </li>
              ))}
            </ul>
          </div>

          {/* Cons */}
          <div>
            <p className="text-sm font-medium text-red-600 mb-3 flex items-center gap-2">
              <X className="w-4 h-4" />
              Cons
            </p>
            <ul className="space-y-2">
              {cons.map((con, index) => (
                <li
                  key={index}
                  className="text-sm text-gray-700 leading-relaxed pl-6 relative"
                >
                  <span className="absolute left-0 top-1.5 w-1.5 h-1.5 bg-red-400 rounded-full" />
                  {con}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// Feature comparison table
interface ComparisonFeature {
  feature: string;
  optionA: boolean | string;
  optionB: boolean | string;
  optionC?: boolean | string;
}

interface ComparisonTableProps {
  optionAName: string;
  optionBName: string;
  optionCName?: string;
  features: ComparisonFeature[];
  highlightColumn?: 'A' | 'B' | 'C';
}

export function ComparisonTable({
  optionAName,
  optionBName,
  optionCName,
  features,
  highlightColumn,
}: ComparisonTableProps) {
  const renderCell = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Check className="w-5 h-5 text-emerald-500 mx-auto" />
      ) : (
        <X className="w-5 h-5 text-gray-300 mx-auto" />
      );
    }
    return <span className="text-sm text-gray-700">{value}</span>;
  };

  return (
    <div className="my-10 not-prose overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="text-left text-sm font-medium text-gray-500 pb-4 pr-4">
              Feature
            </th>
            <th
              className={`text-center text-sm font-semibold pb-4 px-4 ${highlightColumn === 'A' ? 'text-amber-600' : 'text-gray-900'}`}
            >
              {optionAName}
            </th>
            <th
              className={`text-center text-sm font-semibold pb-4 px-4 ${highlightColumn === 'B' ? 'text-amber-600' : 'text-gray-900'}`}
            >
              {optionBName}
            </th>
            {optionCName && (
              <th
                className={`text-center text-sm font-semibold pb-4 px-4 ${highlightColumn === 'C' ? 'text-amber-600' : 'text-gray-900'}`}
              >
                {optionCName}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {features.map((feature, index) => (
            <tr
              key={index}
              className="border-t border-gray-100"
            >
              <td className="py-3 pr-4 text-sm text-gray-700">
                {feature.feature}
              </td>
              <td
                className={`py-3 px-4 text-center ${highlightColumn === 'A' ? 'bg-amber-50' : ''}`}
              >
                {renderCell(feature.optionA)}
              </td>
              <td
                className={`py-3 px-4 text-center ${highlightColumn === 'B' ? 'bg-amber-50' : ''}`}
              >
                {renderCell(feature.optionB)}
              </td>
              {optionCName && (
                <td
                  className={`py-3 px-4 text-center ${highlightColumn === 'C' ? 'bg-amber-50' : ''}`}
                >
                  {renderCell(feature.optionC!)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Simple A vs B comparison
interface VersusProps {
  optionA: {
    name: string;
    description: string;
    points?: string[];
  };
  optionB: {
    name: string;
    description: string;
    points?: string[];
  };
  verdict?: {
    winner: 'A' | 'B' | 'both';
    reason: string;
  };
}

export function Versus({ optionA, optionB, verdict }: VersusProps) {
  return (
    <div className="my-10 not-prose">
      <div className="grid md:grid-cols-2 gap-0 border border-gray-200 rounded-xl overflow-hidden">
        {/* Option A */}
        <div className={`p-6 ${verdict?.winner === 'A' ? 'bg-amber-50' : 'bg-white'}`}>
          <h4
            className="text-xl font-normal text-gray-900 mb-2"
            style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
          >
            {optionA.name}
          </h4>
          <p className="text-sm text-gray-600 mb-4">{optionA.description}</p>
          {optionA.points && (
            <ul className="space-y-2">
              {optionA.points.map((point, index) => (
                <li
                  key={index}
                  className="text-sm text-gray-700 flex items-start gap-2"
                >
                  <Minus className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                  {point}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Option B */}
        <div className={`p-6 border-t md:border-t-0 md:border-l border-gray-200 ${verdict?.winner === 'B' ? 'bg-amber-50' : 'bg-white'}`}>
          <h4
            className="text-xl font-normal text-gray-900 mb-2"
            style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
          >
            {optionB.name}
          </h4>
          <p className="text-sm text-gray-600 mb-4">{optionB.description}</p>
          {optionB.points && (
            <ul className="space-y-2">
              {optionB.points.map((point, index) => (
                <li
                  key={index}
                  className="text-sm text-gray-700 flex items-start gap-2"
                >
                  <Minus className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                  {point}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Verdict */}
      {verdict && (
        <div className="mt-4 bg-gray-900 text-white rounded-lg px-5 py-4 flex items-center gap-4">
          <ArrowRight className="w-5 h-5 text-amber-400 shrink-0" />
          <div>
            <p className="text-sm">
              <span className="font-semibold text-amber-400">
                {verdict.winner === 'A'
                  ? optionA.name
                  : verdict.winner === 'B'
                    ? optionB.name
                    : 'Both options work'}
              </span>
              {' '}{verdict.reason}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
